import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Stack,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Payment as PaymentIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Diamond as DiamondIcon,
  LocalOffer as DiscountIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  Scale as ScaleIcon
} from '@mui/icons-material';

const PaymentManagementDialog = ({
  open,
  onClose,
  transaction,
  customer,
  onPaymentComplete,
  recordPayment,
  // Fine props passed from CustomerLedger
  fineBalance = 0,
  totalFineRequired = 0,
  totalFinePaid = 0
}) => {
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [paymentModes, setPaymentModes] = useState({
    cash: '',
    online: '',
    cheque: '',
    other: '',
    discount: ''
  });
  const [fineEntries, setFineEntries] = useState([]);
  const [paymentNote, setPaymentNote] = useState('');
  const [remainingBalance, setRemainingBalance] = useState(0);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [originalAmount, setOriginalAmount] = useState(0);
  const [outstandingFine, setOutstandingFine] = useState(0);

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    if (transaction) {
      const balance =
        transaction.transactionDetails?.balanceDue ||
        (transaction.summary?.grandTotal - transaction.summary?.paymentDone);
      const original =
        transaction.transactionDetails?.originalBillAmount ||
        transaction.summary?.grandTotal ||
        0;

      setRemainingBalance(Number(balance));
      setOriginalAmount(Number(original));

      // Resolve fine outstanding from props first, else from transaction
      const resolvedFine =
        fineBalance > 0
          ? fineBalance
          : Math.max(
              0,
              (totalFineRequired ||
                transaction.summary?.fineRequired ||
                transaction.transactionDetails?.finePayment?.requiredFine ||
                0) -
                (totalFinePaid ||
                  transaction.summary?.finePaid ||
                  transaction.transactionDetails?.finePayment?.totalFinePaid ||
                  0)
            );
      setOutstandingFine(resolvedFine);

      // Payment history (with backward-compat synthetic entry)
      const history = transaction.paymentDetails?.paymentHistory || [];
      const paymentDone = transaction.summary?.paymentDone || 0;

      if (paymentDone > 0 && history.length === 0) {
        const syntheticPayment = {
          id: `synthetic_${Date.now()}`,
          date: transaction.date,
          amount: paymentDone,
          paymentModes: {
            cash: transaction.paymentDetails?.cash || 0,
            online: transaction.paymentDetails?.online || 0,
            cheque: transaction.paymentDetails?.cheque || 0,
            other: transaction.paymentDetails?.other || 0,
            discount: transaction.paymentDetails?.discount || 0,
            fine: transaction.paymentDetails?.finePayment?.entries?.[0] || null,
            advanceBreakup:
              transaction.paymentDetails?.advancePayment?.breakup || {}
          },
          note: 'Initial payment (historical)',
          recordedBy: transaction.metaDetails?.handledBy || 'System'
        };
        setPaymentHistory([syntheticPayment]);
      } else {
        setPaymentHistory(history);
      }
    }
  }, [transaction, fineBalance, totalFineRequired, totalFinePaid]);

  // Reset inputs each time dialog opens
  useEffect(() => {
    if (open) {
      setPaymentModes({
        cash: '',
        online: '',
        cheque: '',
        other: '',
        discount: ''
      });
      setFineEntries([]);
      setPaymentNote('');
      setPaymentDate(new Date());
    }
  }, [open]);

  // ==================== CALCULATIONS ====================
  const calculateTotalPayment = useCallback(() => {
    return Object.values(paymentModes).reduce(
      (sum, val) => sum + (parseFloat(val) || 0),
      0
    );
  }, [paymentModes]);

  // Total fine being paid (in grams) from all entries
  const calculateTotalFine = useMemo(() => {
    return fineEntries.reduce((sum, entry) => {
      const weight = parseFloat(entry.weight) || 0;
      const purity = parseFloat(entry.purity) || 0;
      return sum + (weight * purity) / 100;
    }, 0);
  }, [fineEntries]);

  const currentCashPaid = calculateTotalPayment();
  const currentFinePaid = calculateTotalFine;

  const remainingCashAfter = useMemo(
    () => Math.max(0, remainingBalance - currentCashPaid),
    [remainingBalance, currentCashPaid]
  );

  const remainingFineAfter = useMemo(() => {
    return Math.round((outstandingFine - currentFinePaid) * 1000) / 1000;
  }, [outstandingFine, currentFinePaid]);

  const hasFineOutstanding = outstandingFine > 0;
  const isCashOverpay = currentCashPaid > remainingBalance + 0.01;
  const isFineOverpay = currentFinePaid > outstandingFine + 0.0001;
  const isNothingEntered = currentCashPaid === 0 && currentFinePaid === 0;

  // ==================== HANDLERS ====================
  const handlePaymentModeChange = (mode, value) => {
    // allow empty or valid decimal input
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setPaymentModes((prev) => ({ ...prev, [mode]: value }));
  };

  const handleAddFineEntry = () => {
    setFineEntries((prev) => [
      ...prev,
      {
        id: `fine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        metal: 'silver',
        weight: '',
        purity: '100'
      }
    ]);
  };

  const handleFineEntryChange = (id, field, value) => {
    setFineEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;
        if (
          (field === 'weight' || field === 'purity') &&
          value !== '' &&
          !/^\d*\.?\d*$/.test(value)
        ) {
          return entry;
        }
        return { ...entry, [field]: value };
      })
    );
  };

  const handleRemoveFineEntry = (id) => {
    setFineEntries((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAutoFillCash = () => {
    setPaymentModes((prev) => ({
      ...prev,
      cash: remainingBalance.toString()
    }));
  };

  const handleAutoFillFine = () => {
    if (!hasFineOutstanding) return;
    setFineEntries([
      {
        id: `fine_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        metal: 'silver',
        weight: outstandingFine.toFixed(3),
        purity: '100'
      }
    ]);
  };

  // ==================== SUBMIT ====================
  const handleSubmitPayment = async () => {
    if (isNothingEntered) {
      alert('Please enter payment amount or fine entry');
      return;
    }

    if (isCashOverpay) {
      alert(
        `Payment cannot exceed remaining balance of ₹${remainingBalance.toFixed(
          2
        )}`
      );
      return;
    }

    if (isFineOverpay) {
      alert(
        `Fine payment cannot exceed outstanding fine of ${outstandingFine.toFixed(
          3
        )}g`
      );
      return;
    }

    setLoading(true);

    // Format date as dd/mm/yyyy with leading apostrophe (your existing convention)
    const day = paymentDate.getDate();
    const month = paymentDate.getMonth() + 1;
    const year = paymentDate.getFullYear();
    const paymentDateValue = `'${day}/${month}/${year}`;

    // Valid fine entries only
    const validFineEntries = fineEntries
      .filter((e) => parseFloat(e.weight) > 0)
      .map((e) => ({
        metal: e.metal || 'silver',
        weight: parseFloat(e.weight) || 0,
        purity: parseFloat(e.purity) || 0
      }));

    // Primary fine = first entry (used by handleRecordPayment's single-object path)
    const primaryFine =
      validFineEntries.length > 0 ? validFineEntries[0] : null;

    const paymentData = {
      amount: currentCashPaid, // cash-only amount
      date: paymentDateValue,
      paymentModes: {
        cash: parseFloat(paymentModes.cash) || 0,
        online: parseFloat(paymentModes.online) || 0,
        cheque: parseFloat(paymentModes.cheque) || 0,
        other: parseFloat(paymentModes.other) || 0,
        discount: parseFloat(paymentModes.discount) || 0,
        advanceBreakup: {},
        // Fine payment object (matches handleRecordPayment expectation)
        fine: primaryFine,
        // All fine entries (optional, if you want to persist all)
        fineEntries: validFineEntries
      },
      note: paymentNote,
      recordedBy: 'Cashier'
    };

    console.log('Submitting payment data:', paymentData);

    try {
      const result = await recordPayment(transaction.id, paymentData);

      if (result.success) {
        setPaymentModes({
          cash: '',
          online: '',
          cheque: '',
          other: '',
          discount: ''
        });
        setFineEntries([]);
        setPaymentNote('');
        setPaymentDate(new Date());

        onPaymentComplete(result.data);
        onClose();
      } else {
        alert(result.message || 'Failed to record payment');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert(error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  // ==================== HISTORY HELPERS ====================
  const totalPaid = paymentHistory.reduce((sum, p) => sum + (p.amount || 0), 0);

  // Total fine paid from history (for display)
  const totalFinePaidHistory = useMemo(() => {
    return paymentHistory.reduce((sum, p) => {
      const modes = p.paymentModes || {};
      if (modes.fine?.weight && modes.fine?.purity) {
        return (
          sum +
          (parseFloat(modes.fine.weight) || 0) *
            (parseFloat(modes.fine.purity) || 0) /
            100
        );
      }
      // multi-entry case
      if (Array.isArray(modes.fineEntries)) {
        return (
          sum +
          modes.fineEntries.reduce((s, e) => {
            return (
              s +
              (parseFloat(e.weight) || 0) *
                (parseFloat(e.purity) || 0) /
                100
            );
          }, 0)
        );
      }
      return sum;
    }, 0);
  }, [paymentHistory]);

  const totalFineRequiredDisplay =
    totalFineRequired ||
    transaction?.summary?.fineRequired ||
    transaction?.transactionDetails?.finePayment?.requiredFine ||
    0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <PaymentIcon color="primary" />
            <Typography variant="h6">Payment Management</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* ============ BILL INFORMATION ============ */}
          <Paper sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom>
              Bill Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Bill Number:{' '}
                  <strong>{transaction?.transactionDetails?.billNo}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Bill Date: <strong>{transaction?.date}</strong>
                </Typography>
                {customer && (
                  <Typography variant="body2" color="textSecondary">
                    Customer: <strong>{customer.name}</strong>
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary">
                  Total Amount: <strong>₹{originalAmount.toFixed(2)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Paid: <strong>₹{totalPaid.toFixed(2)}</strong>
                </Typography>
                <Typography variant="body2" color="error">
                  Remaining Cash: <strong>₹{remainingBalance.toFixed(2)}</strong>
                </Typography>
                {hasFineOutstanding && (
                  <Typography
                    variant="body2"
                    sx={{ color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
                  >
                    <DiamondIcon sx={{ fontSize: 14 }} />
                    Fine Outstanding:{' '}
                    <strong>{outstandingFine.toFixed(3)} g</strong>
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Paper>

          {/* ============ PAYMENT PROGRESS (Cash) ============ */}
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Cash Payment Progress
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    height: 8,
                    bgcolor: 'grey.200',
                    borderRadius: 4,
                    overflow: 'hidden'
                  }}
                >
                  <Box
                    sx={{
                      width: `${
                        originalAmount > 0
                          ? Math.min(100, (totalPaid / originalAmount) * 100)
                          : 0
                      }%`,
                      height: '100%',
                      bgcolor:
                        totalPaid >= originalAmount
                          ? 'success.main'
                          : 'primary.main',
                      transition: 'width 0.3s ease'
                    }}
                  />
                </Box>
              </Box>
              <Typography variant="body2" fontWeight="bold">
                {originalAmount > 0
                  ? ((totalPaid / originalAmount) * 100).toFixed(1)
                  : '0.0'}
                %
              </Typography>
            </Box>
          </Box>

          {/* ============ FINE PROGRESS (if applicable) ============ */}
          {totalFineRequiredDisplay > 0 && (
            <Box>
              <Typography
                variant="body2"
                color="textSecondary"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
              >
                <DiamondIcon sx={{ fontSize: 14 }} />
                Fine Payment Progress
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      height: 8,
                      bgcolor: 'grey.200',
                      borderRadius: 4,
                      overflow: 'hidden'
                    }}
                  >
                    <Box
                      sx={{
                        width: `${
                          totalFineRequiredDisplay > 0
                            ? Math.min(
                                100,
                                (totalFinePaidHistory / totalFineRequiredDisplay) *
                                  100
                              )
                            : 0
                        }%`,
                        height: '100%',
                        bgcolor:
                          totalFinePaidHistory >= totalFineRequiredDisplay
                            ? 'success.main'
                            : 'info.main',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {totalFinePaidHistory.toFixed(3)}g /{' '}
                  {totalFineRequiredDisplay.toFixed(3)}g
                </Typography>
              </Box>
            </Box>
          )}

          {/* ============ PAYMENT HISTORY ============ */}
          {paymentHistory.length > 0 && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                display="flex"
                alignItems="center"
                gap={1}
              >
                <HistoryIcon fontSize="small" />
                Payment History ({paymentHistory.length} payments)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Payment Modes</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentHistory.map((payment, index) => {
                      const modes = payment.paymentModes || {};
                      const hasFine = !!modes.fine?.weight;
                      const hasDiscount = modes.discount > 0;
                      return (
                        <TableRow
                          key={payment.id}
                          sx={{
                            bgcolor:
                              index === paymentHistory.length - 1
                                ? 'action.hover'
                                : 'inherit'
                          }}
                        >
                          <TableCell>
                            {payment?.date?.toString()?.slice(1)}
                          </TableCell>
                          <TableCell align="right">
                            <strong>₹{(payment.amount || 0).toFixed(2)}</strong>
                          </TableCell>
                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              flexWrap="wrap"
                            >
                              {modes.cash > 0 && (
                                <Chip
                                  size="small"
                                  label={`Cash: ₹${modes.cash}`}
                                />
                              )}
                              {modes.online > 0 && (
                                <Chip
                                  size="small"
                                  label={`Online: ₹${modes.online}`}
                                />
                              )}
                              {modes.cheque > 0 && (
                                <Chip
                                  size="small"
                                  label={`Cheque: ₹${modes.cheque}`}
                                />
                              )}
                              {modes.other > 0 && (
                                <Chip
                                  size="small"
                                  label={`Other: ₹${modes.other}`}
                                />
                              )}
                              {hasDiscount && (
                                <Chip
                                  size="small"
                                  icon={<DiscountIcon sx={{ fontSize: 12 }} />}
                                  label={`Discount: ₹${modes.discount}`}
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {hasFine && (
                                <Chip
                                  size="small"
                                  icon={<DiamondIcon sx={{ fontSize: 12 }} />}
                                  label={`Fine: ${(
                                    (parseFloat(modes.fine.weight) || 0) *
                                    (parseFloat(modes.fine.purity) || 0) /
                                    100
                                  ).toFixed(3)}g (${
                                    modes.fine.metal || 'silver'
                                  } ${modes.fine.purity}%)`}
                                  color="info"
                                />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{payment.note}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ============ NEW PAYMENT SECTION ============ */}
          {(remainingBalance > 0 || hasFineOutstanding) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Record New Payment
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DatePicker
                      label="Payment Date"
                      value={paymentDate}
                      onChange={setPaymentDate}
                      format="dd/MM/yyyy"
                      slotProps={{
                        textField: { fullWidth: true, size: 'small' }
                      }}
                    />
                  </Grid>

                  {/* ===== CASH / OTHER PAYMENT SECTION ===== */}
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      gutterBottom
                    >
                      Cash & Other Payments
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Cash"
                      value={paymentModes.cash}
                      onChange={(e) =>
                        handlePaymentModeChange('cash', e.target.value)
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Online"
                      value={paymentModes.online}
                      onChange={(e) =>
                        handlePaymentModeChange('online', e.target.value)
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Cheque"
                      value={paymentModes.cheque}
                      onChange={(e) =>
                        handlePaymentModeChange('cheque', e.target.value)
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      label="Other"
                      value={paymentModes.other}
                      onChange={(e) =>
                        handlePaymentModeChange('other', e.target.value)
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        )
                      }}
                    />
                  </Grid>

                  {/* ===== DISCOUNT ===== */}
                  <Grid item xs={6}>
                    <TextField
                      label="Discount"
                      value={paymentModes.discount}
                      onChange={(e) =>
                        handlePaymentModeChange('discount', e.target.value)
                      }
                      fullWidth
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">₹</InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <DiscountIcon
                              fontSize="small"
                              sx={{ color: 'warning.main' }}
                            />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(245, 158, 11, 0.06)'
                        }
                      }}
                    />
                  </Grid>

                  {/* Quick action buttons */}
                  <Grid item xs={6}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        onClick={handleAutoFillCash}
                        disabled={remainingBalance <= 0}
                        sx={{ textTransform: 'none', height: 40 }}
                      >
                        Fill Cash (₹{remainingBalance.toFixed(0)})
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                {/* ===== FINE PAYMENT SECTION ===== */}
                {hasFineOutstanding && (
                  <Box sx={{ mt: 3 }}>
                    <Divider sx={{ mb: 2 }}>
                      <Chip
                        icon={<DiamondIcon />}
                        label="Fine (Metal) Payment"
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    </Divider>

                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        Outstanding fine:{' '}
                        <strong>{outstandingFine.toFixed(3)} g</strong>. Add
                        one or more metal pieces with their weight and purity.
                        Fine weight = weight × purity / 100.
                      </Typography>
                    </Alert>

                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        size="small"
                        onClick={handleAddFineEntry}
                        sx={{
                          textTransform: 'none',
                          background:
                            'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                          '&:hover': {
                            background:
                              'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                          }
                        }}
                      >
                        Add Metal Piece
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleAutoFillFine}
                        sx={{
                          textTransform: 'none',
                          color: 'info.main',
                          borderColor: 'info.main'
                        }}
                      >
                        Auto-fill Full Fine
                      </Button>
                    </Stack>

                    {/* Fine Entries List */}
                    {fineEntries.length === 0 ? (
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 2,
                          textAlign: 'center',
                          bgcolor: 'action.hover'
                        }}
                      >
                        <Typography variant="body2" color="textSecondary">
                          No fine entries yet. Click "Add Metal Piece" to
                          begin.
                        </Typography>
                      </Paper>
                    ) : (
                      <Stack spacing={1.5}>
                        {fineEntries.map((entry) => {
                          const entryWeight = parseFloat(entry.weight) || 0;
                          const entryPurity = parseFloat(entry.purity) || 0;
                          const entryFine =
                            (entryWeight * entryPurity) / 100;

                          return (
                            <Paper
                              key={entry.id}
                              variant="outlined"
                              sx={{ p: 2 }}
                            >
                              <Grid container spacing={2} alignItems="center">
                                {/* Metal type */}
                                <Grid item xs={12} sm={3}>
                                  <FormControl fullWidth size="small">
                                    <InputLabel>Metal</InputLabel>
                                    <Select
                                      value={entry.metal}
                                      onChange={(e) =>
                                        handleFineEntryChange(
                                          entry.id,
                                          'metal',
                                          e.target.value
                                        )
                                      }
                                      label="Metal"
                                    >
                                      <MenuItem value="silver">
                                        Silver
                                      </MenuItem>
                                      <MenuItem value="gold">Gold</MenuItem>
                                    </Select>
                                  </FormControl>
                                </Grid>

                                {/* Weight */}
                                <Grid item xs={6} sm={3}>
                                  <TextField
                                    label="Weight (g)"
                                    value={entry.weight}
                                    onChange={(e) =>
                                      handleFineEntryChange(
                                        entry.id,
                                        'weight',
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    placeholder="0.000"
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          g
                                        </InputAdornment>
                                      )
                                    }}
                                  />
                                </Grid>

                                {/* Purity */}
                                <Grid item xs={6} sm={3}>
                                  <TextField
                                    label="Purity"
                                    value={entry.purity}
                                    onChange={(e) =>
                                      handleFineEntryChange(
                                        entry.id,
                                        'purity',
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    size="small"
                                    placeholder="100"
                                    InputProps={{
                                      endAdornment: (
                                        <InputAdornment position="end">
                                          %
                                        </InputAdornment>
                                      )
                                    }}
                                  />
                                </Grid>

                                {/* Computed fine + delete */}
                                <Grid item xs={12} sm={3}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    justifyContent="flex-end"
                                  >
                                    <Box
                                      sx={{
                                        px: 1.5,
                                        py: 0.75,
                                        borderRadius: 1.5,
                                        bgcolor: 'success.light',
                                        color: 'success.contrastText',
                                        minWidth: 80,
                                        textAlign: 'center'
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Fine
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: '0.85rem'
                                        }}
                                      >
                                        {entryFine.toFixed(3)}g
                                      </Typography>
                                    </Box>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() =>
                                        handleRemoveFineEntry(entry.id)
                                      }
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Stack>
                                </Grid>
                              </Grid>
                            </Paper>
                          );
                        })}
                      </Stack>
                    )}

                    {/* Fine totals summary */}
                    {fineEntries.length > 0 && (
                      <Alert
                        severity="info"
                        sx={{ mt: 2 }}
                        icon={<ScaleIcon />}
                      >
                        Total fine being paid:{' '}
                        <strong>{currentFinePaid.toFixed(3)} g</strong> |
                        Remaining after payment:{' '}
                        <strong>{remainingFineAfter.toFixed(3)} g</strong>
                      </Alert>
                    )}
                  </Box>
                )}

                {/* ===== NOTE ===== */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <TextField
                      label="Note (Optional)"
                      value={paymentNote}
                      onChange={(e) => setPaymentNote(e.target.value)}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      placeholder="e.g., Partial payment, Cheque number, etc."
                    />
                  </Grid>
                </Grid>

                {/* ===== LIVE SUMMARY ===== */}
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Alert
                      severity={
                        isCashOverpay || isFineOverpay ? 'error' : 'info'
                      }
                      icon={
                        isCashOverpay || isFineOverpay ? (
                          <WarningIcon />
                        ) : undefined
                      }
                    >
                      <Typography variant="body2">
                        <strong>Cash Payment:</strong> ₹
                        {currentCashPaid.toFixed(2)} |{' '}
                        <strong>Remaining Cash:</strong> ₹
                        {remainingCashAfter.toFixed(2)}
                      </Typography>
                      {hasFineOutstanding && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          <strong>Fine Payment:</strong>{' '}
                          {currentFinePaid.toFixed(3)}g |{' '}
                          <strong>Remaining Fine:</strong>{' '}
                          {remainingFineAfter.toFixed(3)}g
                        </Typography>
                      )}
                    </Alert>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {/* ===== FULLY PAID (cash + fine) ===== */}
          {remainingBalance === 0 && !hasFineOutstanding && (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Box display="flex" alignItems="center" gap={1}>
                <ReceiptIcon />
                <Typography>
                  This bill is fully paid! No further payments needed.
                </Typography>
              </Box>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {(remainingBalance > 0 || hasFineOutstanding) && (
          <Button
            onClick={handleSubmitPayment}
            variant="contained"
            color="primary"
            disabled={
              isNothingEntered ||
              isCashOverpay ||
              isFineOverpay ||
              loading
            }
            startIcon={
              loading ? <CircularProgress size={20} /> : <PaymentIcon />
            }
          >
            {loading ? 'Processing...' : 'Record Payment'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PaymentManagementDialog;